/**
 * /workspaces/:wsId/settings — Settings landing page (#851).
 *
 * Three-card index that mirrors the Global Admin landing shape:
 * each card represents a top-level Settings section (Account /
 * Workspace / Organization) and click-targets the section's
 * wrapper, which renders its own pill sub-nav for the tabs inside.
 * Replaces the prior left-rail + right-pane monolith (PR #547,
 * shipped May 2026) — the new layout aligns Settings with
 * `/admin` and gives each section room to grow without crowding
 * the sidebar.
 *
 * **Card visibility** derives from each wrapper's tab gates:
 *   - Account is always visible (every signed-in user has tabs)
 *   - Workspace card hides for roles with zero visible workspace
 *     tabs (Reviewer per ADR 0010 decision 3; also any user with
 *     no workspace role at all)
 *   - Organization card hides for Org Members per #851 Option A —
 *     Member's old read-only-only tabs (General / Members / Teams
 *     / Workspaces with all controls disabled) were misleading
 *     under a "Settings" header. The card hides; if Member needs
 *     org directory info, that becomes a separate surface.
 *
 * **Legacy `?tab=section.id` redirect.** Old shell linked tabs as
 * `?tab=account.profile` / `?tab=organization.audit` / etc. Those
 * URLs (bookmarks, email links, operator playbooks, the org→
 * settings redirect in `OrgSettingsRedirect.tsx`) `<Navigate>`-
 * forward to the new path-based URL inline at the top of this
 * component. The mapping is mechanical: `section.id` → `section/id`
 * with a single special case for `account.password` → `password`
 * (no slug change needed, the legacy `password` id stays).
 */
import { useMemo } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { useAppContext } from '@/hooks/useAppContext'
import { useIdentity } from '@/hooks/useIdentity'
import { useOrgEntitlements } from '@/hooks/useOrgEntitlements'
import { OrgWorkspacePicker } from '@/components/layout/OrgWorkspacePicker'
import { cn } from '@/lib/utils'
import type { OrgWithRole } from '@/lib/api'
import { SETTINGS_SECTIONS } from './sections'
import { getVisibleWorkspaceTabs } from './Workspace'
import { getVisibleOrganizationTabs } from './Organization'

export default function SettingsPage() {
  const { workspace } = useAppContext()
  const { state } = useIdentity()
  const [searchParams] = useSearchParams()

  // Legacy `?tab=section.id` redirect. Pre-#851 deep links land
  // here; forward them to the new path so bookmarks + email links
  // don't 404. Section.id slugs map 1:1 to the new URL segments —
  // no remapping table needed.
  const legacyTab = searchParams.get('tab')
  if (legacyTab && /^(account|workspace|organization)\.[a-z-]+$/.test(legacyTab)) {
    const [section, id] = legacyTab.split('.', 2)
    return <Navigate to={`${section}/${id}`} replace />
  }

  // Resolve org + effective workspace role so the card-visibility
  // predicates have the inputs they need. Pre-ready state renders
  // nothing — short-lived flicker rather than a wrong-content flash.
  const org = useMemo<OrgWithRole | undefined>(() => {
    if (state.status !== 'ready' || !workspace?.orgId) return undefined
    return state.identity.orgs.find((o) => o._id === workspace.orgId)
  }, [state, workspace?.orgId])

  const effectiveWorkspaceRole = useMemo<'admin' | 'tester' | 'reviewer' | undefined>(() => {
    if (state.status !== 'ready' || !workspace?._id) return undefined
    // Org Owner has implicit Workspace Admin per ADR 0010.
    if (org?.role === 'owner') return 'admin'
    return state.identity.workspaceRoles?.[workspace._id]
  }, [state, workspace?._id, org?.role])

  // Threaded into the Organization card's tab count so the count
  // matches the wrapper's sub-nav exactly (#1106). The card never
  // hinges on the feature-gated tab alone — owner/admin always have
  // role-only org tabs — but keeping the same predicate here avoids a
  // count drift if every remaining org tab ever becomes feature-gated.
  const { hasFeature } = useOrgEntitlements(org?._id)

  if (!workspace || state.status !== 'ready') return null

  // Same billing signal the Organization wrapper uses, so the card's tab
  // count matches the sub-nav exactly. The Billing entry never flips the
  // card on its own (a billing-role user always has Usage too), but passing
  // the identical predicate keeps the two counts from drifting.
  const billing = {
    enabled: state.identity.billingEnabled ?? false,
    handoffUrl: state.identity.billingHandoffUrl,
    singleOrg: state.identity.orgs.length === 1,
  }

  // Per-section visibility — `'account'` is always shown; the
  // other two ask their wrapper "any visible tabs for this role?"
  const sectionsVisible = SETTINGS_SECTIONS.filter((s) => {
    if (s.kind === 'account') return true
    if (s.kind === 'workspace') return getVisibleWorkspaceTabs(effectiveWorkspaceRole).length > 0
    if (s.kind === 'organization') return getVisibleOrganizationTabs(org?.role, hasFeature, billing).length > 0
    return false
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Account, workspace, and organization preferences. Pick a section
          below to drill in.
        </p>
      </div>

      {/* Switch context before drilling in — same picker the prior
          shell anchored at the top of the page. Without it,
          changing workspace/org context requires backing out to
          the sidebar; keeping it inline matches how operators
          actually navigate Settings (per #547 user-feedback). */}
      <OrgWorkspacePicker activeWorkspaceId={workspace._id} variant="inline" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sectionsVisible.map((s) => {
          const Icon = s.icon
          const contextLabel =
            s.kind === 'workspace' ? workspace.name
            : s.kind === 'organization' ? org?.name
            : undefined
          return (
            <Link
              key={s.kind}
              to={s.path}
              className="block focus:outline-none focus:ring-2 focus:ring-ring rounded-lg"
            >
              <div
                className={cn(
                  'border rounded-lg p-5 h-full transition-colors',
                  'hover:bg-muted/40 hover:border-primary/40',
                )}
              >
                <div className="flex items-start gap-3">
                  <Icon className={cn('h-7 w-7 flex-shrink-0', s.iconColor)} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-base font-semibold leading-none">{s.label}</h2>
                      {contextLabel && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-muted text-muted-foreground">
                          {contextLabel}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">{s.description}</p>
                    {s.children && s.children.length > 0 && (
                      <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                        {s.children.map((c) => (
                          <li key={c.path} className="flex items-center gap-1">
                            <span aria-hidden="true">·</span>
                            <span className="font-medium text-foreground/80">{c.label}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}
